import { useMemo, useCallback } from 'react';

/**
 * useFlowViewProps Hook
 * 
 * Assembles all props needed for the FlowchartView component.
 */
const useFlowViewProps = ({
  flowchartRef,
  tasks,
  statuses,
  localSprint,
  allSprints = [],
  loadFullSprint,
  calculatedProgress,
  settings,
  cardDisplaySettingsPerView,
  filtersPerView,
  showActionableOnly,
  isTemplate = false,
  actions,
  handlers,
  modals,
  nested,
  toolbar,
  handleNestTasks,
  handleCompileMissionsIntoNestedCampaign,
  handleExplodeNestedCampaign,
  handleTrashNestedSprint,
  setDetailSidebarTaskId,
  setAttachmentsModalTaskId,
  onSelectSprint,
  onSprintIconChange,
  onNestCampaign,
  flowViewVisibility,
  onFlowViewVisibilityChange,
  hideViewExplainerModal,
  onHideViewExplainerModalChange,
  // Handlers from parent
  handleDeleteTaskWithNestedCheck,
  handleDeleteTasksWithNestedCheck,
  handleIconChangeWithNestedSync,
  // Chain actions
  chainActions,
  // View navigation (for "Open in view" button)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  // Multi-campaign paste support
  openCampaignPickerForPaste,
  // Deliverables warning modal
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  isMultiCampaignMode = false,
  onTidyToolbarBusyChange,
  wrapLastUndoAction,
  setLocalSprint,
}) => {
  const applyTaskPositions = useCallback((posMap) => {
    if (!posMap || Object.keys(posMap).length === 0) return;
    setLocalSprint((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map((t) => {
          const pos = posMap[t.id];
          return pos ? { ...t, x: pos.x, y: pos.y } : t;
        }),
      };
    });
  }, [setLocalSprint]);

  // Handle "go to task in view" - dispatches focusMission event
  const handleGoToTaskInView = useCallback((taskId, viewType) => {
    // Dispatch event for the target view to handle focusing
    window.dispatchEvent(new CustomEvent('focusMission', {
      detail: { missionId: taskId, view: viewType }
    }));
  }, []);

  return useMemo(() => ({
    ref: flowchartRef,
    tasks,
    statuses,
    allCategories: actions.getAllCategories(),
    allTags: actions.getAllTags(),
    nestedSprints: nested.nestedSprints,
    autoArrange: toolbar.autoArrange,
    localSprint,
    allSprints,
    loadFullSprint,
    campaignId: localSprint?.id || '',
    sprintName: localSprint?.name,
    sprintIcon: localSprint?.icon,
    sprintProgress: calculatedProgress,
    breadcrumbPath: nested.breadcrumbPath,
    trackpadPanSensitivity: settings.trackpadPanSensitivity,
    trackpadZoomSensitivity: settings.trackpadZoomSensitivity,
    onNavigateToParent: nested.handleGoToParent,
    onSelectSprint,
    onStatusChange: modals.handleStatusChangeWithModal,
    onDecisionAnswerChange: actions.handleDecisionAnswerChange,
    onDecisionOptionsChange: actions.handleDecisionOptionsChange,
    onDependencyChange: actions.handleDependencyChange,
    onConditionsChange: actions.handleConditionsChange,
    onEditTask: actions.handleEditTask,
    onDeleteTask: handleDeleteTaskWithNestedCheck,
    onDeleteTasks: handleDeleteTasksWithNestedCheck,
    onCreateConnectedTask: actions.handleCreateConnectedTask,
    onAddTask: actions.handleAddTask,
    onRenameTask: actions.handleRenameTask,
    onCategoryChange: actions.handleCategoryChange,
    onAddTag: actions.handleAddTag,
    onRemoveTag: actions.handleRemoveTag,
    onSetPrimaryTag: actions.handleSetPrimaryTag,
    onFocusModeChange: toolbar.handleFocusModeChange,
    onIconChange: handleIconChangeWithNestedSync,
    onIconDeleted: actions.clearDeletedIconFromAllTasks,
    onTaskResize: actions.handleTaskResize,
    onTaskResizeEnd: actions.handleTaskResizeEnd,
    onTaskResetSize: actions.handleTaskResetSize,
    onPositionChange: handlers.handlePositionChange,
    onPositionChangeBatch: handlers.handlePositionChangeBatch,
    wrapLastUndoAction,
    onApplyTaskPositions: applyTaskPositions,
    onOpenNestedSprint: nested.handleOpenNestedSprint,
    onCreateNestedSprint: nested.handleCreateNestedSprint,
    onRemoveNestedSprint: handleTrashNestedSprint,
    onDeliverablesChange: actions.handleDeliverablesChange,
    onInputsChange: actions.handleInputsChange,
    onNestTasks: handleNestTasks,
    onCompileMissionsIntoNestedCampaign: handleCompileMissionsIntoNestedCampaign,
    onExplodeNestedCampaign: handleExplodeNestedCampaign,
    onPasteTasks: handlers.handlePasteTasks,
    onAddStatus: handlers.handleAddStatus,
    onDeleteStatus: handlers.handleDeleteStatus,
    onRenameStatus: handlers.handleRenameStatus,
    onRenameCategory: actions.handleRenameCategory,
    onDeleteCategory: actions.handleDeleteCategory,
    onRenameTag: actions.handleRenameTag,
    onDeleteTag: actions.handleDeleteTag,
    onRenameSprint: handlers.handleRenameSprint,
    onSprintIconChange,
    onInsertNodeOnEdge: actions.handleInsertNodeOnEdge,
    onInsertMilestoneOnEdge: handlers.handleInsertMilestoneOnEdge,
    onOpenDetails: setDetailSidebarTaskId,
    onOpenAttachments: setAttachmentsModalTaskId,
    onDueDateChange: actions.handleDueDateChange,
    onRemindersChange: actions.handleRemindersChange,
    onEstimatedDurationMinutesChange: actions.handleEstimatedDurationMinutesChange,
    onCreatedAtChange: actions.handleCreatedAtChange,
    onStartedAtChange: actions.handleStartedAtChange,
    onCompletedAtChange: actions.handleCompletedAtChange,
    onUpdatedAtChange: actions.handleUpdatedAtChange,
    onPriorityChange: actions.handlePriorityChange,
    onDifficultyChange: actions.handleDifficultyChange,
    onCustomFieldValueChange: actions.handleCustomFieldValueChange,
    customFields: localSprint?.customFields || [],
    onArchiveTask: actions.handleArchiveTask,
    onArchiveTasks: actions.handleArchiveTasks,
    onUnarchiveTask: actions.handleUnarchiveTask,
    onSetStatusAutoSync: actions.handleSetStatusAutoSync,
    onMilestoneAnchorChange: actions.handleMilestoneAnchorChange,
    onAddMilestoneOnHost: handlers.handleAddMilestoneOnHost,
    onAddNoteOnHost: handlers.handleAddNoteOnHost,
    onCreateParallelMission: handlers.handleCreateParallelMission,
    onInsertMissionBefore: handlers.handleInsertMissionBefore,
    cardDisplaySettings: cardDisplaySettingsPerView?.flow,
    onNestCampaign,
    flowViewVisibility,
    onFlowViewVisibilityChange,
    hideViewExplainerModal,
    onHideViewExplainerModalChange,
    filters: filtersPerView?.flow || {},
    showActionableOnly,
    isTemplate,
    isMultiCampaignMode,
    initialFocusedTaskIds: toolbar.focusedTaskIds, // For restoring FOKUS state after tab switch
    // Chain management
    chains: localSprint?.chains || [],
    onChainMoveLeft: chainActions ? chainActions.handleChainMoveLeft : undefined,
    onChainMoveRight: chainActions ? chainActions.handleChainMoveRight : undefined,
    // View navigation
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    onGoToTaskInView: handleGoToTaskInView,
    // Multi-campaign paste support
    openCampaignPickerForPaste,
    // Deliverables warning modal
    onSubmitDeliverable,
    // Deliverables editing (for conditions modal)
    onEditDeliverables,
    onClearDeliverable,
    onTidyToolbarBusyChange,
  }), [
    flowchartRef, tasks, statuses, actions, nested, toolbar, localSprint, allSprints, loadFullSprint,
    calculatedProgress, settings, modals, handlers, handleNestTasks, handleCompileMissionsIntoNestedCampaign,
    handleExplodeNestedCampaign,
    handleTrashNestedSprint, setDetailSidebarTaskId, setAttachmentsModalTaskId, onSelectSprint, onSprintIconChange,
    cardDisplaySettingsPerView, handleIconChangeWithNestedSync, handleDeleteTaskWithNestedCheck,
    handleDeleteTasksWithNestedCheck, onNestCampaign, flowViewVisibility, onFlowViewVisibilityChange,
    hideViewExplainerModal, onHideViewExplainerModalChange, filtersPerView, showActionableOnly, isTemplate, isMultiCampaignMode,
    chainActions, currentView, customViews, activeCustomViewId, onViewChange, onSelectCustomView, handleGoToTaskInView,
    openCampaignPickerForPaste, onSubmitDeliverable, onEditDeliverables, onClearDeliverable,
    onTidyToolbarBusyChange, wrapLastUndoAction, applyTaskPositions,
  ]);
};

export default useFlowViewProps;
